import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vehiclesApi } from '../../api/fleet';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export const VEHICLE_STATUS_LABELS: Record<string, string> = {
  available: 'Available', reserved: 'Reserved', dispatched: 'Dispatched', on_mission: 'On Mission',
  under_maintenance: 'Under Maintenance', out_of_service: 'Out of Service', accident_damaged: 'Accident/Damaged', retired: 'Retired',
};
const VEHICLE_TYPE_LABELS: Record<string, string> = { sedan: 'Sedan', suv: 'SUV', pickup: 'Pickup', truck: 'Truck', bus: 'Bus', motorcycle: 'Motorcycle', other: 'Other' };

interface CreateVehicleForm {
  registrationNumber: string; vehicleType: string; make?: string; model?: string; year?: number; fuelType?: string;
}

export function VehiclesListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateVehicleForm>();

  const { data, isLoading } = useQuery({ queryKey: ['vehicles', statusFilter], queryFn: () => vehiclesApi.list({ status: statusFilter || undefined }) });

  const createMutation = useMutation({
    mutationFn: (input: CreateVehicleForm) => vehiclesApi.create({ ...input, year: input.year ? Number(input.year) : undefined }),
    onSuccess: (result) => {
      showSuccess(`Vehicle ${result.data.vehicleReference} registered.`);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/fleet/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const vehicles = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Fleet / Vehicles" description="Vehicle register, dispatch, fuel, and maintenance." />

      <SectionHeader title="Vehicles" actions={
        <>
          <a onClick={() => navigate('/logistics/vehicle-requests')} className="table-link">View Vehicle Requests →</a>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(VEHICLE_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'Register Vehicle'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Registration Number <input {...register('registrationNumber', { required: true })} /></label>
          <label>Vehicle Type
            <select {...register('vehicleType', { required: true })}>
              {Object.entries(VEHICLE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Make <input {...register('make')} /></label>
          <label>Model <input {...register('model')} /></label>
          <label>Year <input type="number" {...register('year')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Register</button>
        </form>
      )}

      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={vehicles}
          rowKey={(v) => v.id}
          columns={[
            { header: 'Registration', render: (v) => <a onClick={() => navigate(`/logistics/fleet/${v.id}`)} className="table-link">{v.registration_number}</a> },
            { header: 'Make/Model', render: (v) => `${v.make ?? ''} ${v.model ?? ''}`.trim() || '-' },
            { header: 'Type', render: (v) => VEHICLE_TYPE_LABELS[v.vehicle_type] ?? v.vehicle_type },
            { header: 'Odometer', render: (v) => v.current_odometer },
            { header: 'Next Service', render: (v) => v.next_service_date ? formatDate(v.next_service_date) : '-' },
            { header: 'Status', render: (v) => <StatusBadge status={v.status} label={VEHICLE_STATUS_LABELS[v.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
